import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Icon } from '@lnr-v2/ui';
import { Modal } from '../../atoms/Modal';
import { Button } from '../../atoms/Button';
import {
  useCreateTranslation,
  useTranslationsByPage,
} from '../../../hooks/useTranslations';
import { getSectionConfig } from '../../../utils/sectionMappings';
import type { TranslationCreateInput } from '../../../services/translations.service';
import styles from './TranslationCreateModal.module.css';
import {
  BASE_ELEMENTS,
  PAGE_OPTIONS,
  SECTION_OPTIONS,
  DEFAULT_FORM_DATA,
} from './constant';

export interface TranslationCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: string;
}

interface FormData {
  page: string;
  section: string;
  element: string;
  textEN: string;
  textFR: string;
}

interface FormErrors {
  page?: string;
  section?: string;
  element?: string;
  textEN?: string;
  textFR?: string;
  duplicate?: string;
}

/**
 * Get the next available number for an element pattern
 */
const getNextNumber = (base: string, existingElements: string[]): number => {
  const pattern = new RegExp(`^${base}_(\\d+)$`);
  let maxNum = 0;
  existingElements.forEach((el) => {
    const match = el.match(pattern);
    if (match) {
      maxNum = Math.max(maxNum, parseInt(match[1], 10));
    }
  });
  return maxNum + 1;
};

/**
 * Format element ID to human-readable label
 */
const formatElementName = (element: string): string => {
  return element
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Auto-generate label from section config and element
 */
const generateLabel = (section: string, element: string): string => {
  const sectionConfig = getSectionConfig(section);
  return `${sectionConfig.label} - ${formatElementName(element)}`;
};

export const TranslationCreateModal: React.FC<TranslationCreateModalProps> = ({
  isOpen,
  onClose,
  currentPage,
}) => {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const createMutation = useCreateTranslation();
  const { data: existingTranslations } = useTranslationsByPage(formData.page);

  // Get existing elements in the current section
  const existingElementsInSection = useMemo(() => {
    if (!existingTranslations || !formData.section) return [];
    return existingTranslations
      .filter((t) => t.section === formData.section)
      .map((t) => t.element);
  }, [existingTranslations, formData.section]);

  // Generate smart suggestions based on input and existing elements
  const filteredSuggestions = useMemo(() => {
    const query = formData.element.toLowerCase();
    const suggestions: { value: string; description: string }[] = [];

    BASE_ELEMENTS.forEach((base) => {
      // Check if base element matches query
      if (
        !query ||
        base.value.includes(query) ||
        base.description.toLowerCase().includes(query)
      ) {
        // Check if base element already exists
        const baseExists = existingElementsInSection.includes(base.value);

        if (!baseExists) {
          // Suggest the base element if it doesn't exist
          suggestions.push({
            value: base.value,
            description: base.description,
          });
        }

        // Always suggest the next numbered version
        const nextNum = getNextNumber(base.value, existingElementsInSection);
        if (nextNum <= 10) {
          // Limit to reasonable numbers
          suggestions.push({
            value: `${base.value}_${nextNum}`,
            description: `${base.description} #${nextNum}`,
          });
        }
      }
    });

    // Filter out duplicates and already existing elements
    const filtered = suggestions
      .filter((s) => !existingElementsInSection.includes(s.value))
      .filter((s, i, arr) => arr.findIndex((x) => x.value === s.value) === i);

    // Sort: exact matches first, then by relevance
    return filtered
      .sort((a, b) => {
        if (query) {
          const aStartsWith = a.value.startsWith(query);
          const bStartsWith = b.value.startsWith(query);
          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;
        }
        return a.value.localeCompare(b.value);
      })
      .slice(0, 8);
  }, [formData.element, existingElementsInSection]);

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...DEFAULT_FORM_DATA, page: currentPage || 'home' });
      setErrors({});
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [isOpen, currentPage]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isDuplicate = useMemo(() => {
    if (!formData.page || !formData.section || !formData.element) return false;
    return existingTranslations?.some(
      (t) =>
        t.page === formData.page &&
        t.section === formData.section &&
        t.element === formData.element
    );
  }, [existingTranslations, formData.page, formData.section, formData.element]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleElementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setFormData((prev) => ({ ...prev, element: value }));
    setShowSuggestions(true);
    setSelectedSuggestionIndex(-1);
    if (errors.element) {
      setErrors((prev) => ({ ...prev, element: undefined }));
    }
  };

  const handleSelectSuggestion = (value: string) => {
    setFormData((prev) => ({ ...prev, element: value }));
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleElementKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          handleSelectSuggestion(
            filteredSuggestions[selectedSuggestionIndex].value
          );
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      case 'Tab':
        setShowSuggestions(false);
        break;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.page) newErrors.page = 'Page is required';
    if (!formData.section.trim()) {
      newErrors.section = 'Section is required';
    }
    if (!formData.element.trim()) {
      newErrors.element = 'Element is required';
    } else if (!/^[a-z_0-9]+$/.test(formData.element)) {
      newErrors.element = 'Lowercase letters, numbers, and underscores only';
    }
    if (!formData.textEN.trim()) newErrors.textEN = 'English text is required';
    if (!formData.textFR.trim()) newErrors.textFR = 'French text is required';
    if (isDuplicate) {
      newErrors.duplicate = `A translation with this combination already exists`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const section = formData.section.trim();
    const element = formData.element.trim();

    const submitData: TranslationCreateInput = {
      page: formData.page,
      section,
      element,
      label: generateLabel(section, element),
      textEN: formData.textEN.trim(),
      textFR: formData.textFR.trim(),
    };

    try {
      await createMutation.mutateAsync(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to create translation:', error);
    }
  };

  const handleClose = () => {
    if (!createMutation.isPending) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create Translation"
      size="medium"
      closeOnBackdropClick={!createMutation.isPending}
      closeOnEscapeKey={!createMutation.isPending}
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        {errors.duplicate && (
          <div className={styles.duplicateWarning}>
            <Icon name="alert-triangle" size="sm" color="currentColor" />
            <span>{errors.duplicate}</span>
          </div>
        )}

        {/* Page Selection */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Page</label>
          <div className={styles.pageGrid}>
            {PAGE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, page: option.value }))
                }
                className={`${styles.pageOption} ${
                  formData.page === option.value ? styles.pageOptionActive : ''
                }`}
              >
                <Icon name={option.icon} size="sm" color="currentColor" />
                <span>{option.label}</span>
              </button>
            ))}
          </div>
          {errors.page && <span className={styles.error}>{errors.page}</span>}
        </div>

        {/* Section Selection */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>
            Section
            {formData.page === 'cases' && (
              <span
                style={{
                  fontWeight: 'normal',
                  color: '#666',
                  marginLeft: '8px',
                }}
              >
                (Enter the case slug, e.g., &quot;project-alpha&quot;)
              </span>
            )}
          </label>
          {/* For "cases" page, show text input for case slug */}
          {formData.page === 'cases' ? (
            <input
              type="text"
              name="section"
              value={formData.section}
              onChange={handleChange}
              className={styles.input}
              placeholder="Enter case slug (e.g., my-project-name)"
            />
          ) : (
            <div className={styles.sectionGrid}>
              {SECTION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, section: option.value }))
                  }
                  className={`${styles.sectionOption} ${
                    formData.section === option.value
                      ? styles.sectionOptionActive
                      : ''
                  }`}
                  style={
                    formData.section === option.value
                      ? {
                          borderColor: option.color,
                          backgroundColor: `${option.color}15`,
                        }
                      : undefined
                  }
                >
                  <Icon name={option.icon} size="sm" color={option.color} />
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
          {errors.section && (
            <span className={styles.error}>{errors.section}</span>
          )}
        </div>

        {/* Element ID with Autocomplete */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Element ID</label>
          <div className={styles.autocompleteWrapper}>
            <input
              ref={inputRef}
              type="text"
              name="element"
              value={formData.element}
              onChange={handleElementChange}
              onKeyDown={handleElementKeyDown}
              onFocus={() => setShowSuggestions(true)}
              className={styles.input}
              placeholder="title, button_cta, word_1..."
              autoComplete="off"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div ref={suggestionsRef} className={styles.suggestions}>
                {filteredSuggestions.map((suggestion, index) => (
                  <button
                    key={suggestion.value}
                    type="button"
                    className={`${styles.suggestion} ${
                      index === selectedSuggestionIndex
                        ? styles.suggestionSelected
                        : ''
                    }`}
                    onClick={() => handleSelectSuggestion(suggestion.value)}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                  >
                    <code className={styles.suggestionValue}>
                      {suggestion.value}
                    </code>
                    <span className={styles.suggestionDesc}>
                      {suggestion.description}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.element && (
            <span className={styles.error}>{errors.element}</span>
          )}
        </div>

        {/* Preview */}
        {formData.page && formData.section && formData.element && (
          <div className={styles.preview}>
            <div className={styles.previewKey}>
              <Icon name="link" size="sm" color="currentColor" />
              <code>
                {formData.page}.{formData.section}.{formData.element}
              </code>
            </div>
            <div className={styles.previewLabel}>
              {generateLabel(formData.section, formData.element)}
            </div>
          </div>
        )}

        {/* Translations */}
        <div className={styles.translationsGrid}>
          <div className={styles.translationField}>
            <div className={styles.langHeader}>
              <span className={styles.flag}>EN</span>
              <span>English</span>
            </div>
            <textarea
              name="textEN"
              value={formData.textEN}
              onChange={handleChange}
              className={styles.textarea}
              rows={3}
              placeholder="English translation..."
            />
            {errors.textEN && (
              <span className={styles.error}>{errors.textEN}</span>
            )}
          </div>
          <div className={styles.translationField}>
            <div className={styles.langHeader}>
              <span className={styles.flag}>FR</span>
              <span>French</span>
            </div>
            <textarea
              name="textFR"
              value={formData.textFR}
              onChange={handleChange}
              className={styles.textarea}
              rows={3}
              placeholder="Traduction en francais..."
            />
            {errors.textFR && (
              <span className={styles.error}>{errors.textFR}</span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={createMutation.isPending || isDuplicate}
            isLoading={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
